import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { useAnalyticsStore } from "client/Features/Analytics/analyticsStore";
import { useRecentlyOpenedResults } from "client/Features/Search/RecentSearch/Persistence/useRecentlyOpenedResults";
import { useCallback } from "react";
import {
	AppWithNavigationParameters,
	AppWithoutParameters,
	MaintenanceWindowAppModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { RecentlyOpenedItemConfigurationProps, useResolveRecentlyOpenedItemForPersistence } from "./useResolveRecentlyOpenedItemForPersistence";
import { GlobalSearchItemPersistedModel } from "client/Features/Search/RecentSearch/Persistence/models";
import { AlertManager, useAlerts } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil, capitalizeFirstLetter } from "client/Misc/utils";
import { DateString, FormattedText, Space } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	navigateToAddressBookApp,
	navigateToAfasApp,
	navigateToAutotaskApp,
	navigateToAzureApp,
	navigateToCalendarApp,
	navigateToCitrixXenApp,
	navigateToClientlessRdpApp,
	navigateToConnectWiseApp,
	navigateToDocumentsApp,
	navigateToEmailApp,
	navigateToEmbedApp,
	navigateToExcelApp,
	navigateToGoogleApp,
	navigateToHubApp,
	navigateToLiquitApp,
	navigateToLocalApp,
	navigateToLyncApp,
	navigateToMagisterApp,
	navigateToNedapOnsApp,
	navigateToOneNoteApp,
	navigateToPeopleApp,
	navigateToPowerPointApp,
	navigateToSharePointApp,
	navigateToShortcutApp,
	navigateToTeamsApp,
	navigateToToDoApp,
	navigateToTopDeskApp,
	navigateToVilansKickApp,
	navigateToVmwareHorizonApp,
	navigateToWindowsVirtualDesktopApp,
	navigateToWordApp,
	navigateToYammerApp,
	navigateToZaaksysteemApp,
	navigateToZenyaDocApp,
	navigateToZermeloApp
} from "client_legacy/Apps/appNavigation";

export type AppNavigationHandlerProps = {
	id: number;
	parameters: AppNavigationParameters | "unresolved";
	maintenanceWindow: MaintenanceWindowAppModel | null;
	timezone: TimeZone;
} & RecentlyOpenedItemConfigurationProps;

export function useAppNavigationHandler(props: AppNavigationHandlerProps): (() => void) {
	const {
		id,
		maintenanceWindow,
		parameters,
		timezone
	} = props;

	const alertManager: AlertManager = useAlerts();
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);
	const { registerFromRecentlyOpened: registerRecentlyOpenedItem } = useRecentlyOpenedResults();

	const recentlyOpenedItem: GlobalSearchItemPersistedModel = useResolveRecentlyOpenedItemForPersistence(props);

	return useCallback(
		(): void => {
			addAnalyticsAppEvent({ installedAppId: id });

			if (parameters === "unresolved") {
				alertManager.showError(localize("_ThisItemCantBeOpened"));
				console.error("Unable to resolve app navigation parameters for recently opened item.");
				return;
			}

			registerRecentlyOpenedItem(recentlyOpenedItem);

			navigateToApp(
				id,
				parameters,
				maintenanceWindow,
				alertManager,
				timezone
			);
		},
		[
			addAnalyticsAppEvent,
			alertManager,
			id,
			maintenanceWindow,
			parameters,
			recentlyOpenedItem,
			registerRecentlyOpenedItem,
			timezone
		]
	);
}

export type AppNavigationParameters =
	| AppWithNavigationParameters
	| AppWithoutParameters;

export function navigateToApp(
	id: number,
	navigationParameters: AppNavigationParameters,
	maintenanceWindow: MaintenanceWindowAppModel | null,
	alertManager: AlertManager,
	timezone: TimeZone
): void {
	if (isNotNil(maintenanceWindow)) {
		const {endsOn, notificationMessage} = maintenanceWindow;

		alertManager.showInfo(
			<>
				<FormattedText
					text={localize("_TheAppIsInMaintenanceUntil")}
					thingsToReplace={[
						{
							what: "{0}",
							filler: (
								<DateString
									value={endsOn}
									format="DMYT"
									timezone={timezone}
								/>
							)
						}
					]}
				/>
				{notificationMessage && (
					<>
						<Space />
						{capitalizeFirstLetter(notificationMessage)}
						.
					</>
				)}
			</>
		);

		return;
	}

	switch (navigationParameters.appType) {
		case AppType.AddressBook:
			navigateToAddressBookApp();
			break;
		case AppType.Afas:
			navigateToAfasApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Autotask:
			navigateToAutotaskApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.AzureApp:
			navigateToAzureApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.CitrixXenApp:
			navigateToCitrixXenApp({
				id: id
			});
			break;
		case AppType.Documents:
			navigateToDocumentsApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Embed:
			navigateToEmbedApp({
				id: id
			});
			break;
		case AppType.Hub:
			navigateToHubApp();
			break;
		case AppType.LocalApp:
			navigateToLocalApp({
				id: id
			});
			break;
		case AppType.TopDesk:
			navigateToTopDeskApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Url:
			navigateToShortcutApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.VilansKick:
			navigateToVilansKickApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.ClientlessRdp:
			navigateToClientlessRdpApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.ConnectWise:
			navigateToConnectWiseApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Email:
			navigateToEmailApp({
				destinationWindow: navigationParameters.parameters.destinationWindow,
				destinationApp: navigationParameters.parameters.destinationApp
			});
			break;
		case AppType.Google:
			navigateToGoogleApp({
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Liquit:
			navigateToLiquitApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Magister:
			navigateToMagisterApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.NedapOns:
			navigateToNedapOnsApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Office365LyncMeeting:
			navigateToLyncApp();
			break;
		case AppType.Office365Calendar:
			navigateToCalendarApp({
				destinationWindow: navigationParameters.parameters.destinationWindow,
				destinationApp: navigationParameters.parameters.destinationApp
			});
			break;
		case AppType.Office365Excel:
			navigateToExcelApp();
			break;
		case AppType.Office365OneNote:
			navigateToOneNoteApp();
			break;
		case AppType.Office365PowerPoint:
			navigateToPowerPointApp();
			break;
		case AppType.Office365Word:
			navigateToWordApp();
			break;
		case AppType.Office365PeopleScreen:
			navigateToPeopleApp();
			break;
		case AppType.SharePoint:
			navigateToSharePointApp({
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Teams:
			navigateToTeamsApp({
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.ToDo:
			navigateToToDoApp({
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.VmwareHorizonApp:
			navigateToVmwareHorizonApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.WindowsVirtualDesktop:
			navigateToWindowsVirtualDesktopApp({
				id: id,
				client: navigationParameters.parameters.clientKind
			});
			break;
		case AppType.Yammer:
			navigateToYammerApp();
			break;
		case AppType.Zaaksysteem:
			navigateToZaaksysteemApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.Zermelo:
			navigateToZermeloApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		case AppType.ZenyaDoc:
			navigateToZenyaDocApp({
				id: id,
				destinationWindow: navigationParameters.parameters.destinationWindow
			});
			break;
		default:
			logNotImplementedApp(navigationParameters, alertManager);
			break;
	}
}

function logNotImplementedApp(
	app: never,
	alertManager: AlertManager
): void {
	console.error(`We don't have any way we support opening app '${JSON.stringify(app)}'`);
	alertManager.showError(localize("_GeneralErrorMessage"));
}
